import { CANVAS_WIDTH } from "../../game/constants";
import { isMobileViewport } from "../../game/platform";
import type { Rect, UiRects } from "../../game/input";
import type { RenderModel } from "../renderModel";
import { drawButton } from "../ui";
import {
  GAME_FONT,
  mFont,
  mFontPlain,
  resolveTitleWorldMapLabelAnchor,
} from "../renderConstants";

// ── Types ────────────────────────────────────────────────────────────────

export interface TitleDeps {
  ctx: CanvasRenderingContext2D;
  nativeSafeAreaTop: number;
  cityBgCrop: { sx: number; sy: number; sw: number; sh: number; dw: number; dh: number };
  canvasHeight: number;
  hoverPos: { x: number; y: number } | null;
  buildingHover: Record<string, number>;
  phoneImage: HTMLImageElement | null;
  uiPadlockIcon: HTMLImageElement | null;
  drawBtn: (rect: Rect, label: string, options?: Parameters<typeof drawButton>[3]) => void;
  getHoverProgress: (rect: Rect) => number;
  nowSec: () => number;
  nowMs: () => number;
  drawDailyLoginEntryButton: (model: RenderModel, rect: Rect, isGuestUser: boolean) => void;
  drawDailyLoginModal: (model: RenderModel, canvasH: number, isGuestUser: boolean) => Partial<UiRects>;
  updateHoverAnimations: (rects: Rect[]) => void;
}

// ── Extracted screen function ────────────────────────────────────────────

export function drawTitleScreen(deps: TitleDeps, model: RenderModel): UiRects {
  const ctx = deps.ctx;
  const centerX = CANVAS_WIDTH / 2;
  // Profile clickable area (top-left, matches header position)
  const profile: Rect = { x: 4, y: 2, w: isMobileViewport() ? 160 : 120, h: isMobileViewport() ? 42 : 24 };

  // Versus status line / error
  const versusStatus = model.versusStatusLine || model.versusError;
  if (versusStatus) {
    ctx.fillStyle = "#ff6b6b";
    ctx.font = mFontPlain(5);
    ctx.textAlign = "center";
    ctx.fillText(versusStatus.slice(0, 64), centerX, 156);
  }

  // --- Building click rects and labels ---
  // Buildings are drawn in drawBackground for perfect alignment.
  // Here we just compute the click rects from source bounding boxes.
  const safeOff = deps.nativeSafeAreaTop;
  const { sx, sy, sw, sh, dw, dh } = deps.cityBgCrop;

  const mapX = (srcX: number): number => ((srcX - sx) / sw) * dw;
  const mapY = (srcY: number): number => ((srcY - sy) / sh) * dh - safeOff;
  const mapW = (srcW: number): number => (srcW / sw) * dw;
  const mapH = (srcH: number): number => (srcH / sh) * dh;

  const buildingDefs = [
    { key: "battle",    srcL: 380, srcT: 659,  srcR: 1362, srcB: 1520, label: "BATTLE",    glowColor: "#ff6b6b" },
    { key: "shop",      srcL: 0,   srcT: 1526, srcR: 499,  srcB: 2235, label: "SHOP",      glowColor: "#d4a8ff" },
    { key: "solo",      srcL: 695, srcT: 2209, srcR: 1040, srcB: 2513, label: "SOLO",      glowColor: "#64d8ff" },
    { key: "lobby",     srcL: 1099,srcT: 1733, srcR: 1536, srcB: 2195, label: "LOBBY",     glowColor: "#5c8cd4" },
    { key: "apartment", srcL: 0,   srcT: 684,  srcR: 366,  srcB: 1442, label: "APARTMENT", glowColor: "#c79dff" },
  ];

  const rects: Record<string, Rect> = {};
  const buildingRectList: Rect[] = [];
  let battleWatchAdRect: Rect | undefined;

  for (let i = 0; i < buildingDefs.length; i++) {
    const def = buildingDefs[i]!;
    const bx = mapX(def.srcL);
    const by = mapY(def.srcT);
    const bw = mapW(def.srcR - def.srcL);
    const bh = mapH(def.srcB - def.srcT);
    const rect: Rect = { x: bx, y: by, w: bw, h: bh };
    rects[def.key] = rect;
    buildingRectList.push(rect);

    const hover = deps.getHoverProgress(rect);
    deps.buildingHover[def.key] = hover;

    // Label only visible on hover (fades in/out)
    const isLocked =
      (def.key === "shop" && !model.featureLocks.shop.unlocked) ||
      (def.key === "lobby" && !model.featureLocks.lobby.unlocked);
    const outOfRamen = def.key === "battle" && model.staminaCurrent <= 0 && !model.staminaPremiumActive;
    if (hover > 0) {
      const labelAnchor = resolveTitleWorldMapLabelAnchor(def.key, rect, centerX);
      ctx.save();
      ctx.globalAlpha = hover;
      ctx.fillStyle = "#ffffff";
      ctx.textAlign = "center";
      ctx.shadowColor = "rgba(0,0,0,0.9)";
      ctx.shadowBlur = 6;
      if (outOfRamen) {
        ctx.font = mFont("bold", 6);
        ctx.textBaseline = "bottom";
        ctx.fillText("OUT OF", labelAnchor.x, labelAnchor.y);
        ctx.textBaseline = "top";
        ctx.fillText("RAMEN", labelAnchor.x, labelAnchor.y + 2);
      } else if (isLocked) {
        const lockLevel = def.key === "shop" ? model.featureLocks.shop.requiredLevel
          : def.key === "lobby" ? model.featureLocks.lobby.requiredLevel : 0;
        // Draw padlock icon above text
        if (deps.uiPadlockIcon) {
          const lockIconSize = 14;
          ctx.drawImage(deps.uiPadlockIcon, labelAnchor.x - lockIconSize / 2, labelAnchor.y - lockIconSize - 12, lockIconSize, lockIconSize);
        }
        ctx.font = mFont("bold", 6);
        ctx.textBaseline = "bottom";
        ctx.fillText("UNLOCKS", labelAnchor.x, labelAnchor.y);
        ctx.textBaseline = "top";
        ctx.fillText(`LEVEL ${lockLevel}`, labelAnchor.x, labelAnchor.y + 2);
      } else {
        ctx.font = mFont("bold", 8);
        ctx.textBaseline = "middle";
        ctx.fillText(def.label, labelAnchor.x, labelAnchor.y);
      }
      ctx.restore();
    }
    // Watch ad button removed — heart tap opens AD_GATE instead
  }

  // ── Onboarding building highlights ──
  // Force hover glow on the highlighted building; drawBackground
  // already desaturates non-highlighted via the onboardingDim → locked path.
  if (model.onboardingStep?.highlightBuilding != null) {
    const highlightKey = model.onboardingStep.highlightBuilding;
    for (const def of buildingDefs) {
      deps.buildingHover[def.key] = def.key === highlightKey ? 1 : 0;
    }
  }

  const canvasH = deps.canvasHeight;

  // Phone button — bottom-right, partially off-screen
  let messageAlertRect: Rect | undefined;
  let messageAlertDismissRect: Rect | undefined;
  const phoneBtnSize = 120;
  const phoneBtnBaseX = CANVAS_WIDTH - phoneBtnSize + 10;
  const phoneBtnY = canvasH - phoneBtnSize - 16;
  // Phone shake animation when new message arrives
  let shakeOffsetX = 0;
  if (model.phoneShakeTimer > 0) {
    shakeOffsetX = Math.sin(deps.nowSec() * 30) * 3 * Math.min(1, model.phoneShakeTimer);
  }
  const phoneBtnX = phoneBtnBaseX + shakeOffsetX;
  let phoneRect: Rect | undefined;
  // Skip normal phone rendering during onboarding — drawOnboardingCall draws the Rafsby phone instead
  if (deps.phoneImage && model.onboardingState == null) {
    const phoneBob = Math.sin(deps.nowSec() * 1.8 + 1) * 1.5;
    // Hover glow effect
    const mouseRect = { x: phoneBtnBaseX, y: phoneBtnY, w: phoneBtnSize, h: phoneBtnSize };
    const hp = deps.hoverPos;
    const isHovered = hp != null && hp.x >= mouseRect.x && hp.x <= mouseRect.x + mouseRect.w && hp.y >= mouseRect.y && hp.y <= mouseRect.y + mouseRect.h;
    if (isHovered) {
      ctx.save();
      ctx.shadowColor = "#64d8ff";
      ctx.shadowBlur = 16 + Math.sin(deps.nowSec() * 4) * 4;
      ctx.drawImage(deps.phoneImage, phoneBtnX, phoneBtnY + phoneBob, phoneBtnSize, phoneBtnSize);
      ctx.restore();
    } else {
      ctx.drawImage(deps.phoneImage, phoneBtnX, phoneBtnY + phoneBob, phoneBtnSize, phoneBtnSize);
    }
    phoneRect = mouseRect;
    // Onboarding phone highlight glow
    if (model.onboardingStep?.type === "highlight-phone") {
      const pulse = 10 + Math.sin(deps.nowMs() / 250) * 5;
      ctx.save();
      ctx.shadowColor = "#ffd700";
      ctx.shadowBlur = pulse;
      ctx.strokeStyle = "rgba(255,215,0,0.8)";
      ctx.lineWidth = 2;
      ctx.strokeRect(mouseRect.x, mouseRect.y, mouseRect.w, mouseRect.h);
      ctx.restore();
    }
    // Unread badge
    if (model.unreadPhoneCount > 0) {
      const badgeX = phoneBtnX + phoneBtnSize * 0.6;
      const badgeY = phoneBtnY + phoneBtnSize * 0.15;
      const badgeR = 10;
      ctx.fillStyle = "#ff4444";
      ctx.beginPath();
      ctx.arc(badgeX, badgeY, badgeR, 0, Math.PI * 2);
      ctx.fill();
      ctx.strokeStyle = "#0a1020";
      ctx.lineWidth = 1.5;
      ctx.stroke();
      ctx.fillStyle = "#ffffff";
      ctx.font = mFont("bold", 6);
      ctx.textAlign = "center";
      ctx.textBaseline = "middle";
      const badgeText = model.unreadPhoneCount > 9 ? "9+" : String(model.unreadPhoneCount);
      ctx.fillText(badgeText, badgeX, badgeY);
    }

    // Message alert bubble — pixel art speech bubble style
    if (model.incomingMessageAlert != null) {
      const alert = model.incomingMessageAlert;
      const dismissSize = 12;
      const px = 2; // pixel size for the blocky look
      const bubbleW = Math.min(130, phoneBtnBaseX);
      // Simple char-count wrap (font measurement unreliable at small canvas sizes)
      const charsPerLine = 22;
      const words = alert.preview.split(" ");
      const previewLines: string[] = [];
      let line = "";
      for (const w of words) {
        const test = line.length > 0 ? `${line} ${w}` : w;
        if (test.length > charsPerLine && line.length > 0) {
          previewLines.push(line);
          line = w;
        } else {
          line = test;
        }
      }
      if (line.length > 0) previewLines.push(line);
      const msgFont = `5px ${GAME_FONT}`;
      const senderFont = `bold 6px ${GAME_FONT}`;
      const lineH = 8;
      const pad = 8;
      const senderH = 10;
      const bubbleH = pad + senderH + previewLines.length * lineH + pad;
      const bubbleX = phoneBtnBaseX - bubbleW + 22;
      const bubbleY = phoneBtnY - bubbleH - 4;
      const borderColor = "#1e3a5f";
      const fillColor = "#101d31";

      ctx.save();
      ctx.imageSmoothingEnabled = false;

      // Draw pixel-art speech bubble shape
      // Main body (inset by px on corners for blocky rounded look)
      ctx.fillStyle = fillColor;
      ctx.fillRect(bubbleX + px, bubbleY, bubbleW - px * 2, bubbleH);
      ctx.fillRect(bubbleX, bubbleY + px, bubbleW, bubbleH - px * 2);

      // Pixel tail — bottom-right, staircase down toward phone
      const tailX = bubbleX + bubbleW - 18;
      ctx.fillRect(tailX, bubbleY + bubbleH, px * 3, px);
      ctx.fillRect(tailX + px, bubbleY + bubbleH + px, px * 3, px);
      ctx.fillRect(tailX + px * 2, bubbleY + bubbleH + px * 2, px * 3, px);

      // Pixel border — top edge
      ctx.fillStyle = borderColor;
      ctx.fillRect(bubbleX + px, bubbleY, bubbleW - px * 2, px);
      // Bottom edge (with gap for tail)
      ctx.fillRect(bubbleX + px, bubbleY + bubbleH - px, tailX - bubbleX - px, px);
      ctx.fillRect(tailX + px * 3, bubbleY + bubbleH - px, bubbleX + bubbleW - px - (tailX + px * 3), px);
      // Left edge
      ctx.fillRect(bubbleX, bubbleY + px, px, bubbleH - px * 2);
      // Right edge
      ctx.fillRect(bubbleX + bubbleW - px, bubbleY + px, px, bubbleH - px * 2);
      // Tail border pixels
      ctx.fillRect(tailX, bubbleY + bubbleH, px, px);
      ctx.fillRect(tailX + px, bubbleY + bubbleH + px, px, px);
      ctx.fillRect(tailX + px * 2, bubbleY + bubbleH + px * 2, px, px);
      ctx.fillRect(tailX + px * 3 + px * 2, bubbleY + bubbleH, px, px);
      ctx.fillRect(tailX + px * 3, bubbleY + bubbleH + px * 2, px * 2, px);

      // X dismiss button (top-right, pixel style)
      const xBtnX = bubbleX + bubbleW - dismissSize - 2;
      const xBtnY = bubbleY + 3;
      ctx.fillStyle = "#8a9ab0";
      ctx.font = `bold 7px ${GAME_FONT}`;
      ctx.textAlign = "center";
      ctx.textBaseline = "middle";
      ctx.fillText("\u00D7", xBtnX + dismissSize / 2, xBtnY + dismissSize / 2);

      // Sender name
      ctx.fillStyle = "#64d8ff";
      ctx.font = senderFont;
      ctx.textAlign = "left";
      ctx.textBaseline = "top";
      ctx.fillText(alert.sender, bubbleX + pad, bubbleY + pad);

      // Preview text (wrapped)
      ctx.fillStyle = "#c0cfe0";
      ctx.font = msgFont;
      for (let i = 0; i < previewLines.length; i++) {
        ctx.fillText(previewLines[i], bubbleX + pad, bubbleY + pad + senderH + i * lineH);
      }

      ctx.restore();

      // Tap target covers bubble + tail
      const tailH = px * 3;
      messageAlertRect = { x: bubbleX, y: bubbleY, w: bubbleW, h: bubbleH + tailH };
      // Dismiss button tap target
      messageAlertDismissRect = { x: xBtnX - 4, y: xBtnY - 4, w: dismissSize + 8, h: dismissSize + 8 };
    }
  }

  // Daily login bonus button — bottom-left title card
  const hoverRectList = [...buildingRectList];
  const isGuestUser = model.authState.user?.id === "guest-local" || model.authState.profile?.provider === "guest";
  const dailyLoginOpenRect: Rect = { x: -6, y: canvasH - 160, w: 140, h: 170 };
  deps.drawDailyLoginEntryButton(model, dailyLoginOpenRect, isGuestUser);
  hoverRectList.push(dailyLoginOpenRect);

  let dailyLoginUi: Partial<UiRects> = {};
  if (model.dailyLoginModalOpen) {
    dailyLoginUi = deps.drawDailyLoginModal(model, canvasH, isGuestUser);
    if (dailyLoginUi.dailyLoginClose != null) {
      hoverRectList.push(dailyLoginUi.dailyLoginClose);
    }
    if (dailyLoginUi.dailyLoginClaim != null) {
      hoverRectList.push(dailyLoginUi.dailyLoginClaim);
    }
    if (dailyLoginUi.dailyLoginSignIn != null) {
      hoverRectList.push(dailyLoginUi.dailyLoginSignIn);
    }
  }

  deps.updateHoverAnimations(hoverRectList);
  const uiRects: UiRects = {
    versusQuickmatch: rects.battle,
    start: rects.solo,
    shop: rects.shop,
    versusMode: rects.lobby,
    profile,
    editProfile: rects.apartment,
    titleWatchAd: battleWatchAdRect,
    dailyLoginOpen: dailyLoginOpenRect,
    phone: phoneRect,
    messageAlert: messageAlertRect,
    messageAlertDismiss: messageAlertDismissRect,
    ...dailyLoginUi
  };
  return uiRects;
}
